'use client'
import Image from 'next/image'
import dynamic from 'next/dynamic'

import { cn } from '@/utils/twMerge'
import React, { useMemo } from 'react'
import { PrismicNextImage } from '@prismicio/next'
import { getDescription, getHeading } from '@/utils/getHeroFormData'
import { CMMC_DEMO_FORM_UIDS } from '@/utils/cmmcLandingPages'
import { getExperimentForUid } from '@/utils/experiments'
import useExperiment from '@/hooks/useExperiment'
import { PrismicLink, PrismicRichText, PrismicText } from '@prismicio/react'
import { isFilled, type Content } from '@prismicio/client'

import PartnerPortalCta from '@/components/common/PartnerPortalCta'

const HubSpotForm = dynamic(() => import('@/components/common/HubSpotForm'))

const hideTagTypes = ['repeatable-demo']
// Partner pages that link to Channeltivity instead of a HubSpot form.
type PartnerPortalCtaConfig = Pick<React.ComponentProps<typeof PartnerPortalCta>, 'labelKey' | 'portalLink'>
const PARTNER_PORTAL_HERO_CTAS: Partial<Record<string, PartnerPortalCtaConfig>> = {
  'deal-registration': { labelKey: 'deal_registration_form', portalLink: 'deal_reg' },
  'audit-partner': { labelKey: 'become_a_partner' },
  'reseller-partner': { labelKey: 'become_a_partner' },
  'technology-partner': { labelKey: 'become_a_partner' },
}
const PARTNERS_UID = [
  'service-partner',
  'reseller-partner',
  'audit-partner',
  'technology-partner',
  'gap-assessment',
  'deal-registration',
  'free-trial-demo',
  'newsletter-signup',
  'partner-referral',
  'request-demo-follow-up',
  'partner-request-demo-follow-up',
  'rsa-demo',
  'referral-program',
  'summit',
] as const
const kitSectionPaddingBottomUids = [
  'risk-management-kit',
  'cmmc-compliance-kit',
  'soc-2-compliance-kit',
  'hipaa-compliance-kit',
  'iso-27001-compliance-kit',
  'third-party-risk-management-kit',
]

type DataObject = Record<string, any>
/** Only the `marketing` document fields the partner portal hero actually reads. */
type PartnerPortalHeroData = Pick<
  Content.MarketingDocument['data'],
  'tag' | 'review_image' | 'hero_image' | 'title' | 'description'
>
type ItemObject = Record<string, any>

type HeroFormProps = {
  data: DataObject
  formType?: string
  type?: string
  uid?: string
  locale?: string
  prefillMessage?: string
}

const Heading1: React.FC<{ children: React.ReactNode }> = ({ children }) => <h1>{children}</h1>

// Ongoing hero H1 A/B experiment: heading stays hidden until the variant resolves, so no control-to-test flash.
function ExperimentHeroHeading({
  uid,
  field,
}: {
  uid?: string
  field: React.ComponentProps<typeof PrismicRichText>['field']
}) {
  const experiment = getExperimentForUid(uid)
  const { variant, ready } = useExperiment(experiment?.flagKey)
  const overrideText = experiment?.h1Overrides?.[variant]

  const heading = overrideText ? (
    <Heading1>{overrideText}</Heading1>
  ) : (
    <PrismicRichText field={field} components={{ heading2: ({ children }) => <Heading1>{children}</Heading1> }} />
  )

  if (!experiment) return heading
  return <div className={ready ? undefined : 'invisible'}>{heading}</div>
}

// Helper: Partnership types list
function renderPartnershipTypes(data: DataObject, uid?: string, type?: string, formType?: string) {
  return (
    <div className="types-of-partnership">
      <h4>{data.what_we_cover_text}</h4>
      {uid === 'demo-bottle-offer' ? (
        <PrismicRichText field={data.features} />
      ) : (
        <ul className={cn('grid max-w-full gap-y-3', formType === 'contactPartner' && '!grid-cols-2')}>
          {data.features?.map((item: ItemObject, index: number) => {
            if (item.spans?.[0]?.data?.link_type === 'Web') {
              return (
                <li key={`feature-${index}`}>
                  <a href={item.spans[0].data.url}>{item.text}</a>
                </li>
              )
            }
            return <li key={`feature-${index}`}>{item.text}</li>
          })}
        </ul>
      )}
    </div>
  )
}

// Helper: Left content (demo, marketing, offer)
function renderLeftFormContent(data: DataObject, type?: string, uid?: string, isPartnerUID?: boolean) {
  const requestDemoUIDs = [
    'request-demo-follow-up',
    'partner-request-demo-follow-up',
    'free-trial-demo',
    'secureframe-vs-vanta',
    'secureframe-vs-drata',
    'secureframe-vs-hyperproof',
    'secureframe-vs-zengrc',
    'secureframe-vs-logicgate',
    'partner-referral',
    'cmmc',
    'request-cmmc-demo',
    'rsa-demo',
  ]
  const isRequestDemoType = type === 'request-demo-form' || requestDemoUIDs.includes(uid as string)
  const isMarketingOrOfferType =
    (type === 'marketing' || type === 'offer') &&
    !['request-demo-follow-up', 'partner-request-demo-follow-up', 'rsa-demo'].includes(uid as string)

  return (
    <>
      {isRequestDemoType && (
        <section className="request-demo-content">
          {renderPartnershipTypes(data, uid, type)}
          <PrismicNextImage field={data.review_image} sizes="100vw" style={{ height: 'auto', width: '100%' }} fallbackAlt="" />
        </section>
      )}
      {isMarketingOrOfferType && (
        <section className="marketing-offer-content">
          {!isPartnerUID && type === 'offer' && <PrismicLink href="/contact/partner">Are you a partner?</PrismicLink>}
          {![
            'soc-2-compliance-kit-ungated',
            'iso-27001-compliance-kit-ungated',
            'partner-referral',
            'cmmc',
            'request-cmmc-demo',
            'cmmc-compliance-kit-ungated',
            'cybersecurity-awareness-kit-ungated',
          ].includes(uid as string) && renderPartnershipTypes(data, uid, type)}
          {!requestDemoUIDs.includes(uid as string) && (
            <div
              className={cn(
                ['offer', 'marketing', 'request-demo-form'].includes(type as string) &&
                  'mt-8 [&>p]:m-0 [&>p]:text-lg [&>p]:text-primary-ink-100 [&_a]:text-primary-green-150',
              )}>
              <PrismicRichText field={data.for_general_questions_text} />
            </div>
          )}
        </section>
      )}
    </>
  )
}

// Copy and images come from the Prismic `marketing` document so they stay editable and localised.
function PartnerPortalHero({
  data,
  type,
  uid,
  cta,
}: {
  data: PartnerPortalHeroData
  type?: string
  uid?: string
  cta: PartnerPortalCtaConfig
}) {
  // Deal registration keeps its original layout; the partner program pages share the matched one.
  const matched = uid !== 'deal-registration'

  return (
    <section className={cn(uid ?? '', 'marketing relative z-[1]')}>
      {/* Matched: capped at 1920px wide, and 900px tall on desktop to fit the longest copy (audit, 881px)
          with shorter copy centered. */}
      <div className={cn('lg:grid lg:grid-cols-2', matched && 'mx-auto max-w-[1920px] lg:min-h-[900px]')}>
        {/* Left: copy. On desktop the box is pushed right to line up with the site container gutter. */}
        <div className={cn('flex justify-end', matched && 'justify-start lg:items-center lg:justify-end')}>
          <div className="w-full max-w-[640px] px-4 py-12 sm:px-6 lg:py-24 lg:pl-8 lg:pr-16">
            <PrismicNextImage field={data.review_image} width={120} height={120} className="mb-8" fallbackAlt="" />
            {data.tag && (
              <span className="mb-6 block text-sm font-semibold uppercase text-primary-green-85">{data.tag}</span>
            )}
            <div className="[&_h1]:text-[calc(2.325rem+.9vw)] [&_h1]:tracking-[-1.28px] [&_h1]:xl:text-[4rem]">
              <ExperimentHeroHeading uid={uid} field={getHeading(type, data)} />
            </div>
            <div className="mb-10 mt-6 [&>p+p]:mt-4 [&>p]:text-primary-ink-85">
              <PrismicRichText field={getDescription(type, data)} />
            </div>
            <PartnerPortalCta {...cta} source={uid} />
          </div>
        </div>

        {/* Right: full-bleed photo; matched pages crop from above center so faces stay in frame */}
        {isFilled.image(data.hero_image) && (
          <div className={cn('relative h-[320px] w-full sm:h-[420px] lg:h-auto', !matched && 'lg:min-h-[640px]')}>
            <PrismicNextImage
              field={data.hero_image}
              fill
              sizes="(max-width: 1024px) 100vw, 50vw"
              className={cn('object-cover', matched && 'object-[50%_30%]')}
              fallbackAlt=""
            />
          </div>
        )}
      </div>
    </section>
  )
}

const HERO_FORM_BG_CLASSES =
  'absolute right-0 top-0 -z-[1] h-auto w-full [&>img]:h-[850px] [&>img]:sm:h-[550px] [&>img]:md:h-[600px] [&>img]:lg:h-auto sm:left-0 sm:right-auto'

// Helper: Hero form left content for the bottle offer demo
function BottleOfferLeftContent({
  data,
  type,
  showTagDesktopViewTag,
  uid,
  isPartnerUID,
}: {
  data: DataObject
  type?: string
  showTagDesktopViewTag: boolean
  uid?: string
  isPartnerUID: boolean
}) {
  return (
    <div className="flex flex-col gap-y-8 lg:flex-row lg:gap-x-8">
      <PrismicNextImage field={data.hero_image} width={180} height={608} fallbackAlt="" />
      <div className="basis-[calc(100%-180px)]">
        {showTagDesktopViewTag && (
          <span className="mb-6 hidden text-sm font-semibold uppercase text-primary-green-85 lg:block">{data.tag}</span>
        )}
        <PrismicRichText
          field={getHeading(type, data)}
          components={{ heading2: ({ children }) => <Heading1>{children}</Heading1> }}
        />
        <PrismicRichText field={getDescription(type, data)} />
        {renderLeftFormContent(data, type, uid, isPartnerUID)}
      </div>
    </div>
  )
}

// Helper: Hero form left content for all other cases
function DefaultLeftContent({
  data,
  type,
  uid,
  showTagDesktopViewTag,
  formType,
  isPartnerUID,
}: {
  data: DataObject
  type?: string
  uid?: string
  showTagDesktopViewTag: boolean
  formType?: string
  isPartnerUID: boolean
}) {
  return (
    <>
      {showTagDesktopViewTag && (
        <span
          className={cn(
            uid === 'summit'
              ? 'ai-hero-btn-tag mb-6 hidden !w-fit rounded-[45px] border border-white/30 px-8 py-3 text-primary-green-85 lg:block'
              : 'mb-6 hidden text-sm font-semibold uppercase text-primary-green-85 lg:block',
          )}>
          {data.tag}
        </span>
      )}
      {uid === 'summit' && (
        <span className="mb-4 text-sm font-semibold uppercase text-primary-green-85 lg:hidden">{data.tag}</span>
      )}
      {uid === 'startupstack' && <PrismicNextImage field={data.icon} fallbackAlt="" />}
      {uid === 'deal-registration' && (
        <PrismicNextImage field={data.review_image} width={120} height={120} className="mb-5" fallbackAlt="" />
      )}
      {uid === 'summit' && (
        <>
          <Image
            src="/images/summit-logo_mobile.svg"
            alt="summit-logo"
            sizes="100vw"
            width={0}
            height={0}
            className="sm:hidden"
            style={{ height: 'auto', width: '100%' }}
          />
          <Image
            src="/images/summit-logo_desktop.svg"
            alt="summit-logo"
            sizes="100vw"
            width={0}
            height={0}
            className="hidden sm:block"
            style={{ height: 'auto', width: '100%' }}
            priority
          />
        </>
      )}
      <ExperimentHeroHeading uid={uid} field={getHeading(type, data)} />
      <PrismicRichText field={getDescription(type, data)} />
      {formType === 'contactPartner' && renderPartnershipTypes(data, uid, type, formType)}
      {type === 'repeatable-demo' && (
        <>
          <PrismicNextImage field={data.logo} sizes="100vw" style={{ height: 'auto', width: '100%' }} preload fallbackAlt="" />
          {data.show_quote && (
            <div className="mt-5 rounded-md border p-8 shadow-light backdrop-blur-25">
              <p className="!mt-0 lg:px-8">
                <PrismicText field={data.demo_quote} />
              </p>
              <div className="flex items-center">
                <PrismicNextImage field={data.client_avatar} className="h-[80px] w-[80px]" fallbackAlt="" />
                <div className="pl-4">
                  <p className="!mb-1 !mt-0 !text-base">{data.client_name}</p>
                  <p className="!m-0 !text-base">{data.client_designation}</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {renderLeftFormContent(data, type, uid, isPartnerUID)}
    </>
  )
}

const HeroForm: React.FC<HeroFormProps> = ({ data, formType, type, uid, locale, prefillMessage }) => {
  const isPartnerUID = useMemo(() => PARTNERS_UID.includes(uid as (typeof PARTNERS_UID)[number]), [uid])
  const shouldUseDemoEmbeddedForm =
    formType === 'demoForm' || formType === 'demoEmbeddedForm' || CMMC_DEMO_FORM_UIDS.includes(uid as string)
  const showBecomePartnerText =
    !isPartnerUID &&
    type !== 'request-demo-form' &&
    !CMMC_DEMO_FORM_UIDS.includes(uid as string) &&
    (formType === 'demoForm' || type === 'marketing')
  const showTagDesktopViewTag = !hideTagTypes.includes(type as string)

  // `data` is the loosely-typed document bag shared by every hero variant; narrow it at the boundary.
  const partnerPortalCta = uid ? PARTNER_PORTAL_HERO_CTAS[uid] : undefined
  if (partnerPortalCta)
    return <PartnerPortalHero data={data as PartnerPortalHeroData} type={type} uid={uid} cta={partnerPortalCta} />

  return (
    <section
      className={cn(
        uid ?? '',
        type ?? '',
        'relative z-[1] pt-8 lg:pt-0',
        kitSectionPaddingBottomUids.includes(uid as string) && 'pb-20 lg:pb-[140px]',
      )}>
      <div className={HERO_FORM_BG_CLASSES}>
        <Image
          src="/images/background-pattern-mobile/480@2x.png"
          alt="hero-bg"
          width={0}
          height={0}
          sizes="100vw"
          priority
          className="sm:hidden"
          style={{ width: '100%', height: 'auto' }}
        />
        <Image
          src="/images/hero-form-bg.png"
          alt="hero-bg"
          width={0}
          height={0}
          sizes="100vw"
          priority
          className="hidden sm:block"
          style={{ width: '100%', height: 'auto' }}
        />
      </div>
      <div className="container">
        <div className={cn('flex flex-col-reverse lg:flex-row', uid === 'summit' && 'flex-col')}>
          {/* Left */}
          <div
            className={cn(
              'hero-form-content-left',
              uid === 'demo-bottle-offer' && 'lg:w-[80%] lg:pr-8',
              uid === 'summit' && '!pb-0 !pt-0 lg:!pb-8 lg:!pt-14',
              ['offer', 'marketing', 'request-demo-form'].includes(type as string) && 'lg:!pt-14',
            )}>
            {uid === 'demo-bottle-offer' ? (
              <BottleOfferLeftContent
                data={data}
                type={type}
                showTagDesktopViewTag={showTagDesktopViewTag}
                uid={uid}
                isPartnerUID={isPartnerUID}
              />
            ) : (
              <DefaultLeftContent
                data={data}
                type={type}
                uid={uid}
                showTagDesktopViewTag={showTagDesktopViewTag}
                formType={formType}
                isPartnerUID={isPartnerUID}
              />
            )}
          </div>

          {/* Right */}
          <div className="hero-form-content-right">
            {uid === 'summit' ? (
              // <span className="mb-2 text-sm font-semibold uppercase text-primary-green-85 lg:hidden">{data.tag}</span>
              <></>
            ) : (
              <h2 className="mb-2 lg:hidden">{data.tag}</h2>
            )}
            {[
              'soc-2-compliance-kit-ungated',
              'iso-27001-compliance-kit-ungated',
              'cmmc-compliance-kit-ungated',
              'cybersecurity-awareness-kit-ungated',
            ].includes(uid as string) ? (
              renderPartnershipTypes(data, uid, type)
            ) : (
              <div className="hero-form-wrapper">
                <HubSpotForm
                  uid={uid}
                  locale={locale}
                  type={
                    shouldUseDemoEmbeddedForm
                      ? 'demoEmbeddedForm'
                      : isPartnerUID
                        ? 'contactPartnerEmbeddedForm'
                        : 'ebookEmbeddedForm'
                  }
                  portalId={data.hubspot_form_portal_id}
                  formId={data.hubspot_form_form_id}
                  showBecomePartnerText={showBecomePartnerText}
                  prefillMessage={prefillMessage}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default HeroForm
